import { useCallback, useEffect, useMemo, useRef, useState } from 'react';

const EDGE_PADDING = 16;
const DEFAULT_SIZE = {
  width: 340,
  height: 520,
  collapsedWidth: 78,
  collapsedHeight: 112,
};

function clamp(value, min, max) {
  return Math.min(Math.max(value, min), max);
}

export default function useFloatingTrayPosition(collapsed) {
  const frameRef = useRef(null);
  const dragStateRef = useRef(null);
  const hasCustomPositionRef = useRef(false);
  const [position, setPosition] = useState({ x: 0, y: 0 });

  const getSize = useCallback(
    () => ({
      width: collapsed ? DEFAULT_SIZE.collapsedWidth : DEFAULT_SIZE.width,
      height: collapsed ? DEFAULT_SIZE.collapsedHeight : DEFAULT_SIZE.height,
    }),
    [collapsed]
  );

  const getDockedPosition = useCallback(() => {
    if (typeof window === 'undefined') {
      return { x: 0, y: 0 };
    }

    const { width, height } = getSize();

    return {
      x: Math.max(window.innerWidth - width - EDGE_PADDING, EDGE_PADDING),
      y: Math.max(window.innerHeight - height - EDGE_PADDING, EDGE_PADDING),
    };
  }, [getSize]);

  useEffect(() => {
    setPosition((current) => {
      if (!current.x && !current.y) {
        return getDockedPosition();
      }

      if (!hasCustomPositionRef.current) {
        return getDockedPosition();
      }

      if (typeof window === 'undefined') {
        return current;
      }

      const { width, height } = getSize();

      return {
        x: clamp(current.x, EDGE_PADDING, Math.max(window.innerWidth - width - EDGE_PADDING, EDGE_PADDING)),
        y: clamp(current.y, EDGE_PADDING, Math.max(window.innerHeight - height - EDGE_PADDING, EDGE_PADDING)),
      };
    });
  }, [collapsed, getDockedPosition, getSize]);

  useEffect(() => {
    if (typeof window === 'undefined') {
      return undefined;
    }

    const handleResize = () => {
      setPosition((current) => {
        const { width, height } = getSize();

        return {
          x: clamp(current.x, EDGE_PADDING, Math.max(window.innerWidth - width - EDGE_PADDING, EDGE_PADDING)),
          y: clamp(current.y, EDGE_PADDING, Math.max(window.innerHeight - height - EDGE_PADDING, EDGE_PADDING)),
        };
      });
    };

    window.addEventListener('resize', handleResize);

    return () => {
      window.removeEventListener('resize', handleResize);
    };
  }, [getSize]);

  const stopDragging = useCallback(() => {
    dragStateRef.current = null;
    document.body.style.userSelect = '';
  }, []);

  useEffect(() => {
    const handlePointerMove = (event) => {
      if (!dragStateRef.current || typeof window === 'undefined') {
        return;
      }

      const { width, height } = getSize();
      const nextX = event.clientX - dragStateRef.current.offsetX;
      const nextY = event.clientY - dragStateRef.current.offsetY;
      hasCustomPositionRef.current = true;

      setPosition({
        x: clamp(nextX, EDGE_PADDING, Math.max(window.innerWidth - width - EDGE_PADDING, EDGE_PADDING)),
        y: clamp(nextY, EDGE_PADDING, Math.max(window.innerHeight - height - EDGE_PADDING, EDGE_PADDING)),
      });
    };

    const handlePointerUp = () => {
      stopDragging();
    };

    window.addEventListener('pointermove', handlePointerMove);
    window.addEventListener('pointerup', handlePointerUp);

    return () => {
      window.removeEventListener('pointermove', handlePointerMove);
      window.removeEventListener('pointerup', handlePointerUp);
    };
  }, [getSize, stopDragging]);

  const handleDragStart = useCallback((event) => {
    if (event.button !== 0 && event.pointerType !== 'touch') {
      return;
    }

    const frame = frameRef.current;

    if (!frame) {
      return;
    }

    const rect = frame.getBoundingClientRect();

    dragStateRef.current = {
      offsetX: event.clientX - rect.left,
      offsetY: event.clientY - rect.top,
    };

    document.body.style.userSelect = 'none';
  }, []);

  const frameStyle = useMemo(
    () => ({
      position: 'fixed',
      left: position.x,
      top: position.y,
      zIndex: 1400,
    }),
    [position.x, position.y]
  );

  return {
    dockToCorner: () => {
      hasCustomPositionRef.current = false;
      setPosition(getDockedPosition());
    },
    frameRef,
    frameStyle,
    handleDragStart,
  };
}
